import {FC} from "react";
import {makeStyles} from "tss-react/mui";
import {Box, Typography} from "@mui/material";
import {IAdminUsersPlaylistsResponseEntry} from "../../../../types/admin";
import {Link, useNavigate} from "react-router-dom";
import {AdminHeader} from "../../AdminHeader";
import {AdminTable, AdminTableProps} from "@/components/common/table";
import {GRID_CHECKBOX_SELECTION_COL_DEF} from "@mui/x-data-grid-pro";
import {useApi} from "@buildwitt/shapeshift-identity";
import {IGroupEntity, IPaginatedResponse} from "@buildwitt/shapeshift-types";
import useDevCycleSdk from "@/hooks/useDevCycleSdk";

const useStyles = makeStyles()((theme) => ({
	adminPlaylistsList: {
		"& th": {
			fontSize: "11px",
			fontWeight: 700,
			textTransform: "uppercase"
		}
	},
	tableBadge: {
		padding: theme.spacing(0.2, 1.25)
	},
	readDistribution: {
		width: "100%",
		display: "flex",
		gap: theme.spacing(2),
		"& > p":{
			width: theme.spacing(4),
			textAlign: "center"
		},
		"& > div":{
			width: "100%",
			display: "flex",
			flexDirection: "column",
			justifyContent: "center"
		}
	},
	noPlaylistResponseContainer: {
		display: "flex",
		justifyContent: "center",
		alignItems: "center",
		height: "100%",
		marginTop: theme.spacing(16),
		flexDirection: "column",
		textAlign: "center",
		gap: theme.spacing(2),
		"& h1": {
			fontSize: "34px",
			maxWidth: "550px",
			fontWeight: 600,
			color: theme.palette.primary.main
		},
		"& p": {
			opacity: 0.7,
			color: "#f9f9f9"
		}
	},
	betaDisclaimer: {
		position: "relative",
		fontStyle: "italic",
		top: theme.spacing(-3),
		"a": {
			textDecoration: "underline",
			color: theme.palette.primary.main
		}
	}
}));

const headCells = (): AdminTableProps<"lesson", IAdminUsersPlaylistsResponseEntry>["columns"] => ([
	{
		...GRID_CHECKBOX_SELECTION_COL_DEF,
		type: "string",
		hideable: false
	},
	{
		field: "title",
		type: "string",
		flex: 1,
		headerName: "Title",
		minWidth: 240
	},
	{
		field: "courseCount",
		type: "number",
		flex: 1,
		headerName: "Courses",
		minWidth: 240,
		sortable: false,
		filterable: false,
		headerAlign: "left",
		align: "left"
	},
	{
		field: "enrollmentCount",
		type: "number",
		flex: 1,
		headerName: "Enrolled",
		minWidth: 240,
		sortable: false,
		filterable: false,
		headerAlign: "left",
		align: "left"
	}
]);

export const AdminPlaylistList: FC = () => {
	const {classes} = useStyles();
	const navigate = useNavigate();
	const [apiResult] = useApi<IPaginatedResponse<IGroupEntity[]>>({url: "/api/strapi-plugin-shapeshift-lms/cohort/group", data: {page: 1, pageSize: 0}})
	const {hideCreationButtons} = useDevCycleSdk();

	return (
		<Box className={classes.adminPlaylistsList}>
			<AdminHeader
				title="Playlists (Beta)"
				subtitle="Group courses into playlists to assign and organize content for learners."
				actionButtons={hideCreationButtons ? undefined : [{label: "Create New", onClick: () => navigate("create")}]}
			/>
			<Typography className={classes.betaDisclaimer}>Rough grading complete, working towards final grade. Stand by for finish work. <Link to="https://portal.productboard.com/buildwitt/1-buildwitt-portal/tabs/1-recently-released/tabs/3-recently-released/submit-idea">Click here to provide feedback.</Link></Typography>

			{(!apiResult.isLoading && apiResult.data?.totalCount === 0)
				? <Box className={classes.noPlaylistResponseContainer} >
					<img src="/playlist_cassette.svg" alt="Empty State" />
					<Typography variant="h1">
						It seems like your company doesn’t
						have any playlists yet.
					</Typography>
					<Typography variant="body1">
						You can create playlists by clicking “Create New”.
					</Typography>
				</Box> :
				<AdminTable<"group", IAdminUsersPlaylistsResponseEntry>
					url="/api/strapi-plugin-shapeshift-lms/cohort/group"
					columns={headCells()}
					filterMode="server"
					paginationMode="server"
					sortingMode="server"
					onEditAction={e => navigate(e.id.toString())}
					filterSettingsPersistenceStorageKey="playlistFilterSettings"
					type="group"
				/>}
		</Box>
	);
}
